import type { PlanCue } from '@toolproof-core/lib/_contract/scene-plan';

import { resolveRecordedNarration } from '@/_pure/recordedNarration';
import type { EpisodePlayback } from '@/_contract/episode';
import type { EpisodePlaybackRecording } from '@/_contract/episodePlayback';
import type { EpisodeNarrationCue } from '@/episodeNarration';

/** A recording drives the timeline when it matches the text; without sound, a missing one falls back to timed reading. */
export function synchronizeRecordedEpisodeNarration(
    owner: { synchronize: (cue?: EpisodeNarrationCue) => void },
    recording: EpisodePlaybackRecording & {
        episodeHandle: string; sceneId: string; beatId: string; cues?: readonly Pick<PlanCue, 'id' | 'beforeText'>[] },
    cue: Omit<EpisodeNarrationCue, 'audioUrl' | 'timepoints' | 'wordBoundaries' | 'cues'>,
) {
    const clip = resolveRecordedNarration(recording.clips,
        `${recording.episodeHandle}/${recording.sceneId}/${recording.beatId}`,
        cue.text, recording.catalog, recording.cues, recording.audioBaseUrl, cue.narrationPauses, recording.regenerationHint);
    if (clip.error && cue.sound) {
        owner.synchronize();
        cue.onError(clip.error);
        return;
    }
    owner.synchronize({ ...cue, cues: recording.cues,
        ...(clip.url ? { audioUrl: clip.url, timepoints: clip.timepoints, wordBoundaries: clip.wordBoundaries } : {}) });
}

/**
 * Steps run one beat's timeline; Play runs it and continues; unready cues hold until the visuals reach them.
 * Pausing or suspending holds the timeline in place.
 */
export function synchronizeRecordedEpisodePlayback(
    owner: { synchronize: (cue?: EpisodeNarrationCue) => void; setPaused: (paused: boolean) => void },
    recording: Parameters<typeof synchronizeRecordedEpisodeNarration>[1],
    cue: Parameters<typeof synchronizeRecordedEpisodeNarration>[2],
    playback: EpisodePlayback,
    readiness: { playbackActive?: boolean; animationSuspended?: boolean; onComplete: () => void;
        onCueComplete?: () => void; cueReadyDuringPlayback?: boolean },
) {
    if (playback.error) {
        owner.synchronize();
        return;
    }
    if (readiness.animationSuspended) {
        owner.setPaused(true);
        return;
    }
    if (!playback.isPlaying && !playback.narrationRequested) {
        owner.setPaused(true);
        return;
    }
    const firstCue = recording.cues?.[0];
    if ((playback.cueIndex ?? 0) === 0 && (playback.narrationCueReached ?? -1) < 0
        && firstCue && cue.text.indexOf(firstCue.beforeText) === 0) {
        // Prepare the first checkpoint and its presentation before creating an audio/reading timeline.
        owner.synchronize();
        cue.onMarker?.(0);
        owner.setPaused(true);
        return;
    }
    if (recording.cues?.[playback.cueIndex ?? 0]
        && (playback.narrationCueReached ?? -1) >= (playback.cueIndex ?? 0)) {
        owner.setPaused(true);
        if (readiness.cueReadyDuringPlayback || (playback.status === 'reading' && !readiness.playbackActive)) readiness.onCueComplete?.();
        return;
    }
    if (playback.narrationCompleted) {
        owner.synchronize();
        if (playback.status === 'reading' && !readiness.playbackActive) readiness.onComplete();
        return;
    }
    synchronizeRecordedEpisodeNarration(owner, recording, { ...cue, onMarker: index => {
        if (index < (playback.cueIndex ?? 0)) return true;
        const ready = index === (playback.cueIndex ?? 0)
            && (readiness.cueReadyDuringPlayback || (playback.status === 'reading' && !readiness.playbackActive));
        cue.onMarker?.(index);
        if (ready && readiness.onCueComplete) {
            readiness.onCueComplete();
            return true;
        }
        return false;
    } });
    owner.setPaused(false);
}
